import React, { createContext, useEffect } from "react";
import { KnowYourExecutorAPI } from "apis/KnowYourExecutorAPI";
import {
  ExecutorVerificationStep,
  ExecutorVerificationStepDetails,
  ExecutorVerificationStatus,
} from "models/ExecutorVerification";
import { UserProfile } from "models/UserProfile";
import { UserProfileAPI } from "apis/UserProfileAPI";
import { ExecutorVerificationSteps } from "views/flows/ExecutorVerification";
import { useAuthContext } from "AuthContextWrapper";
interface Props {
  children: JSX.Element;
}

export interface ExecutorVerificationContextInterface {
  steps: ExecutorVerificationStep[] | null;
  setSteps: (steps: ExecutorVerificationStep[]) => void,
  stepDetailsMap: { [key: number]: ExecutorVerificationStepDetails } | null;
  statusesMap: { [key: string]: ExecutorVerificationStatus } | null;
  userProfile: UserProfile;
  loadingUserProfile: boolean;
  setUserProfile: (userProfile: UserProfile) => void,
  verificationStepId: ExecutorVerificationSteps,
  setVerificationStepId: (id: ExecutorVerificationSteps) => void,
  setStepStatusId: (verificationStepId: number, statusId: number) => Promise<any>,
  getStepStatusId: (verificationStepId: number) => number,
}

export const ExecutorVerificationContext =
  createContext<ExecutorVerificationContextInterface>({
    steps: null,
    setSteps: () => undefined,
    stepDetailsMap: null,
    statusesMap: null,
    userProfile: new UserProfile(),
    loadingUserProfile: true,
    setUserProfile: () => undefined,
    verificationStepId: ExecutorVerificationSteps.UNKNOWN,
    setVerificationStepId: () => undefined,
    setStepStatusId: async () => undefined,
    getStepStatusId: () => { return -1 },
  });

export const ExecutorVerificationContextWrapper = ({
  children,
}: Props): JSX.Element => {
  const [steps, setSteps] = React.useState<ExecutorVerificationStep[] | null>(null);
  const [stepDetailsMap, setStepDetailsMap] = React.useState<{ [key: number]: ExecutorVerificationStepDetails } | null>(null);
  const [statusesMap, setStatusesMap] = React.useState<{ [key: string]: ExecutorVerificationStatus } | null>(null);
  const [userProfile, setUserProfile] = React.useState<UserProfile>(new UserProfile());
  const [loadingUserProfile, setLoadingUserProfile] = React.useState<boolean>(true);
  const [verificationStepId, setVerificationStepId] = React.useState<number>(0);
  const { user } = useAuthContext()

  useEffect(() => {
    async function doAsync() {
      if(!user?.id) {
        console.error("no user id found")
        return
      }
      const statuses = await KnowYourExecutorAPI.fetchExecutorVerificationStatuses(user?.id);
      const m: { [key: string]: ExecutorVerificationStatus } = {};
      statuses.map((s) => (m[s.id] = s));
      setStatusesMap(m);
    }
    doAsync();
  }, []);

  useEffect(() => {
    async function doAsync() {
      if(!user?.id) {
        console.error("no user id found")
        return
      }
      setSteps(await KnowYourExecutorAPI.fetchExecutorVerificationSteps(user?.id));
    }
    doAsync();
  }, []);

  useEffect(() => {
    async function doAsync() {
      if(!user?.id) {
        console.error("no user id found")
        return
      }
      const details = await KnowYourExecutorAPI.fetchExecutorVerificationStepDetails(user?.id);
      const m: { [key: number]: ExecutorVerificationStepDetails } = {};
      details.map((d) => (m[d.id] = d));
      setStepDetailsMap(m);
    }
    doAsync();
  }, []);

  useEffect(() => {
    async function doAsync() {
      if(!user?.id) {
        console.error("no user id found")
        return
      }
      setUserProfile(
        await UserProfileAPI.get(user?.id) || new UserProfile()
      )
      setLoadingUserProfile(false)
    }
    doAsync();
  }, []);


  function getStepStatusId(verificationStepId: number): number {
    if (!steps) {
      throw new Error("missing steps to look at")
    }

    // This is the last step in a subflow, so we're going to update the status of this step/subflow
    const stepIndex = steps?.findIndex(s => s.verificationStepId == verificationStepId)
    if (stepIndex === -1 || (stepIndex !== 0 && !stepIndex)) {
      throw new Error("unable to find index of step to return")
    }
    const currentStep = steps[stepIndex]

    return currentStep.statusId
  }

  async function setStepStatusId(verificationStepId: number, statusId: number) {
    if (!steps) {
      throw new Error("missing steps to update")
    }
    if(!user?.id) {
      console.error("no user id found")
      return
    }

    // This is the last step in a subflow, so we're going to update the status of this step/subflow
    const stepIndex = steps?.findIndex(s => s.verificationStepId == verificationStepId)
    if (stepIndex === -1 || (stepIndex !== 0 && !stepIndex)) {
      throw new Error("unable to find index of step to update")
    }
    const currentStep = steps[stepIndex]
    currentStep.statusId = statusId

    // Update step in db  
    const savedStep = await KnowYourExecutorAPI.updateExecutorVerificationStep(user?.id, currentStep)
    if (!savedStep) {
      throw new Error("unable to save verification step")
    }

    // Update local copy of step
    steps[stepIndex] = savedStep
    setSteps(steps)
  }

  return (
    <ExecutorVerificationContext.Provider
      value={{
        steps,
        setSteps,
        stepDetailsMap,
        statusesMap,
        userProfile,
        loadingUserProfile,
        setUserProfile,
        verificationStepId,
        setVerificationStepId,
        setStepStatusId,
        getStepStatusId,
      }}
    >
      {children}
    </ExecutorVerificationContext.Provider>
  );
};
